import type { track as TrackFn } from "pro-features/components/WithTrack/src/context";
import type { Topic } from "app/pages/sales-intelligence/sub-modules/intent/types/common";
import createTrackingEventName from "app/pages/sales-intelligence/utils/testing/createTrackingEventName";
import type { CustomAttributes } from "app/services/track/track";

const createIntentTrackingService = (track: TrackFn) => {
    const trackIntentManagementAction = (actionName: string, eventName: string, customEvent?: CustomAttributes) => {
        track("intent admin", actionName, eventName, customEvent);
    };

    return {
        trackIntentManagementAction: trackIntentManagementAction,
        trackSelectedTopicsAction: () => {
            trackIntentManagementAction("click select topics", "go to select topics");
        },
        trackAddTopicClickAction: (selectedTopic: Topic) => {
            trackIntentManagementAction("click add topic", `add topic/${selectedTopic.name}/${selectedTopic.category}`);
        },
        trackCategoryFilterClickAction: () => {
            trackIntentManagementAction("click category filter", "filter topic category");
        },
        trackApplyCategoryAction: (selectedCategories: string[]) => {
            trackIntentManagementAction(
                "apply categories",
                `select category from filter/${selectedCategories.join(",")}`,
            );
        },
        trackSortTopicAction: () => {
            trackIntentManagementAction("sort topics", `sort intent topics`);
        },
        trackSortCategoriesAction: () => {
            trackIntentManagementAction("sort categories", `sort intent categories`);
        },
        trackSortCompaniesAction: () => {
            trackIntentManagementAction("sort companies", `sort number of companies with intent`);
        },
        trackApplyTopicClickAction: (popupAction: string, selectedTopicsCount?: number) => {
            const eventName = createTrackingEventName("apply topics popup", popupAction, selectedTopicsCount ?? "");

            trackIntentManagementAction("click apply topics", eventName);
        },
        trackRemoveTopicClickAction: (topic: Topic) => {
            trackIntentManagementAction("click remove topic", `remove topic/${topic.name}/${topic.category}`);
        },
        trackOnHoverTopicTips: () => {
            trackIntentManagementAction("hover selection tips", `topic selection tips`);
        },
        trackSelectedTopicsHookAction: () => {
            trackIntentManagementAction("selected topics hook", `need more topics hook`);
        },
        trackUpgradeIntentAction: () => {
            trackIntentManagementAction("click upgrade intent", `upgrade intent`);
        },
        trackManageTopicsAction: () => {
            trackIntentManagementAction("click manage topics", "manage intent topics");
        },
        trackSearchTermIntentAction: (searchTerm: string, topics: readonly Topic[]) => {
            const topicResults = topics
                .slice(0, 5)
                .map((topic: Topic) => topic.name.replace("/", "~"))
                .join(",");
            trackIntentManagementAction("search", `search topic/${searchTerm}/${topicResults}`);
        },
        trackContactUsClickAction: (eventSubName?: string) => {
            trackIntentManagementAction(
                "click contact us",
                `open intent hook${eventSubName ? `/${eventSubName}` : ""}`,
            );
        },
        trackIntentHook: (eventName: string, eventSubName: string) => {
            track("sales intelligence hooks", "click", `${eventName}/${eventSubName}`);
        },
    };
};

export default createIntentTrackingService;
